import { useState, useCallback, useContext, useEffect } from 'react';
import { Company, CompanyService, PaginatedCompanyResponse } from './company.service';
import { AuthContext } from '../../../context/authcontext';

interface PaginationData {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  hasMore: boolean;
  pageSize: number;
}

interface UseCompaniesReturn {
  companies: Company[];
  loading: boolean;
  backgroundLoading: boolean;
  error: string | null;
  pagination: PaginationData;
  fetchCompanies: (page?: number) => Promise<void>;
  refreshCompanies: () => Promise<void>;
  addCompany: (company: Company) => void;
  updateCompany: (company: Company) => void;
  removeCompany: (companyId: number) => void;
  lastFetched: Date | null;
  isInitialized: boolean;
  clearBackgroundLoading: () => void;
}

export const useCompanies = (): UseCompaniesReturn => {
  const { selectedTenantId } = useContext(AuthContext);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [backgroundLoading, setBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetched, setLastFetched] = useState<Date | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [pagination, setPagination] = useState<PaginationData>({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    hasMore: false,
    pageSize: 12
  });

  // Cache to store fetched pages and prevent duplicate requests
  const [fetchedPages, setFetchedPages] = useState<Set<number>>(new Set());
  const [pageData, setPageData] = useState<Map<number, Company[]>>(new Map());

  const clearBackgroundLoading = useCallback(() => {
    setBackgroundLoading(false);
  }, []);

  // Reset data when tenant changes
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;
    
    const debouncedReset = () => {
      timeoutId = setTimeout(() => {
        if (selectedTenantId) {
          setFetchedPages(new Set());
          setPageData(new Map());
          setCompanies([]);
          setIsInitialized(false);
          setError(null);
          setPagination(prev => ({
            ...prev,
            currentPage: 1,
            totalPages: 1,
            totalCount: 0,
            hasMore: false
          }));
        }
      }, 300);
    };

    debouncedReset();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedTenantId]);

  const fetchCompanies = useCallback(async (page: number = 1, forceRefresh: boolean = false) => {
    // Validate tenant is selected
    if (!selectedTenantId) {
      setError('No tenant selected. Please select a tenant to view companies.');
      return;
    }

    // For pagination, always fetch the requested page
    // The cache check is disabled for better pagination experience

    try {
      setLoading(true);
      setError(null);
      
      const response: PaginatedCompanyResponse = await CompanyService.getCompaniesByPage(page, 12);
      
      // Update pagination data
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        totalPages: response.totalPages,
        totalCount: response.totalCount,
        hasMore: response.hasMore,
        pageSize: 12
      }));

      // Cache the page data
      setPageData(prev => new Map(prev).set(page, response.companies));
      setFetchedPages(prev => new Set(prev).add(page));

      // For pagination, replace companies with current page data
      setCompanies(response.companies);

      setLastFetched(new Date());
      setIsInitialized(true);
      
      // Set background loading after main API completes
      setBackgroundLoading(true);
    } catch (err) {
      console.error('Error fetching companies:', err);
      setError('Failed to fetch companies. Please check your tenant selection.');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId]);

  const refreshCompanies = useCallback(async () => {
    setFetchedPages(new Set());
    setPageData(new Map());
    setIsInitialized(false);
    await fetchCompanies(1, true);
  }, [fetchCompanies]);



  const addCompany = useCallback((company: Company) => {
    setCompanies(prev => [company, ...prev]);
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: prev.totalCount + 1
    }));
    // Clear cache since data has changed
    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  const updateCompany = useCallback((updatedCompany: Company) => {
    setCompanies(prev => 
      prev.map(company => 
        company.company_id === updatedCompany.company_id ? updatedCompany : company
      )
    );
    // Update cache as well
    setPageData(prev => {
      const newPageData = new Map(prev);
      newPageData.forEach((companies, pageNum) => {
        const updatedCompanies = companies.map(company =>
          company.company_id === updatedCompany.company_id ? updatedCompany : company
        );
        newPageData.set(pageNum, updatedCompanies);
      });
      return newPageData;
    });
  }, []);

  const removeCompany = useCallback((companyId: number) => {
    setCompanies(prev => prev.filter(company => company.company_id !== companyId));
    // Update pagination count
    setPagination(prev => ({
      ...prev,
      totalCount: Math.max(0, prev.totalCount - 1)
    }));
    // Clear cache since data has changed
    setFetchedPages(new Set());
    setPageData(new Map());
  }, []);

  return {
    companies,
    loading,
    backgroundLoading,
    error,
    pagination,
    fetchCompanies,
    refreshCompanies,
    addCompany,
    updateCompany,
    removeCompany,
    lastFetched,
    isInitialized,
    clearBackgroundLoading
  };
}; 